import {
    CheckCircle2,
    Download,
    RefreshCw,
    Upload,
    X,
    XCircle,
} from 'lucide-react'

import useTranslationStore from '@/language/useTranslationStore.ts'

import type { TransferOverlayItemProps, TransferTask } from '../Types/types'

function formatBytes(bytes: number) {
    if (bytes >= 1024 * 1024) {
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    }

    if (bytes >= 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`
    }

    return `${Math.round(bytes)} B`
}

function getTaskLabel(task: TransferTask, t: (key: string) => string) {
    if (task.kind === 'update') {
        if (task.status === 'success') return t('shared.transfer.updateDone')
        if (task.status === 'error') return t('shared.transfer.updateFailed')
        if (task.stage === 'queued') return t('shared.transfer.updateQueued')

        return task.stage === 'backup'
            ? t('shared.transfer.backupRunning')
            : t('shared.transfer.updateRunning')
    }

    if (task.status === 'success') {
        return task.direction === 'upload'
            ? t('shared.transfer.uploadDone')
            : t('shared.transfer.downloadDone')
    }

    if (task.status === 'error') {
        return task.direction === 'upload'
            ? t('shared.transfer.uploadFailed')
            : t('shared.transfer.downloadFailed')
    }

    return task.direction === 'upload'
        ? t('shared.transfer.uploadRunning')
        : t('shared.transfer.downloadRunning')
}

function getTaskIcon(task: TransferTask) {
    if (task.status === 'success') {
        return <CheckCircle2 className="h-4 w-4 text-emerald-400" />
    }

    if (task.status === 'error') {
        return <XCircle className="h-4 w-4 text-red-400" />
    }

    if (task.kind === 'update') {
        return <RefreshCw className="text-primary h-4 w-4 animate-spin" />
    }

    if (task.direction === 'upload') {
        return <Upload className="text-primary h-4 w-4" />
    }

    return <Download className="text-primary h-4 w-4" />
}

export default function TransferOverlayItem({
    task,
    onDismiss,
}: TransferOverlayItemProps) {
    const progress = Math.max(0, Math.min(100, task.progress))
    const { t } = useTranslationStore()

    return (
        <div className="border-border-dark bg-surface-dark/95 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border text-gray-100 shadow-2xl shadow-black/40 backdrop-blur-md">
            <div className="flex items-start gap-3 p-4">
                <div className="border-border-dark bg-input-dark mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border">
                    {getTaskIcon(task)}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-white">
                                {getTaskLabel(task, t)}
                            </p>
                            <p className="text-secondary-text truncate text-xs">
                                {task.title}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => onDismiss(task.id)}
                            aria-label={t('shared.transfer.dismiss')}
                            className="text-secondary-text hover:bg-input-dark cursor-pointer rounded p-1 transition hover:text-white"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="bg-input-dark mt-3 h-1.5 overflow-hidden rounded-full">
                        <div
                            className="bg-primary h-full rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>

                    <div className="text-secondary-text mt-2 flex items-center justify-between gap-3 text-[11px] font-medium">
                        {task.kind === 'update'
                            ? task.step > 0
                                ? t('shared.transfer.step', {
                                      step: task.step,
                                      totalSteps: task.totalSteps,
                                  })
                                : t('shared.transfer.queue')
                            : t('shared.transfer.progress', {
                                  progress: Math.round(progress),
                                  transferredBytes: formatBytes(
                                      task.transferredBytes,
                                  ),
                                  totalBytes: formatBytes(task.totalBytes),
                              })}
                    </div>

                    {task.message ? (
                        <p className="text-secondary-text mt-2 text-xs">
                            {task.message}
                        </p>
                    ) : null}
                </div>
            </div>
        </div>
    )
}
